import type { AdminUseSelectDto, AdminUseUpdateDto, AdminUsesSelectDto } from '@@types/api.types';
import { ApiErrorWrapper, useApi } from '@hooks/api.hook';
import { queryKeys } from '@queries/keys';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useGetCurrentUser } from './users.query';

export type AdminUsesSortField = 'NAME' | 'STATUS';

export type AdminUsesSortOrder = 'ASC' | 'DESC';

export type UsesQueryParams = {
  limit: number;
  order: AdminUsesSortOrder;
  page: number;
  search?: string;
  sort: AdminUsesSortField;
};

export const useAdminUsesQuery = (params: UsesQueryParams) => {
  const { apiGet } = useApi();
  const { isAdmin } = useGetCurrentUser();

  return useQuery<AdminUsesSelectDto, ApiErrorWrapper>({
    enabled: isAdmin,
    queryFn: async () => {
      return await apiGet<AdminUsesSelectDto>({
        path: '/v1/admin/uses',
        query: params,
      });
    },
    queryKey: queryKeys.adminUses.getUses(params),
    placeholderData: keepPreviousData,
  });
};

export const useAdminUseQuery = (id: string | undefined, preloadedData?: AdminUseSelectDto) => {
  const { apiGet } = useApi();
  const { isAdmin } = useGetCurrentUser();

  const enabled = isAdmin && !!id;

  return useQuery<AdminUseSelectDto, ApiErrorWrapper>({
    enabled,
    initialData: preloadedData,
    queryFn: async () => {
      return await apiGet<AdminUseSelectDto>({
        path: `/v1/admin/uses/${id}`,
      });
    },
    queryKey: queryKeys.adminUses.getUseById(id ?? ''),
  });
};

export const useAdminCreateUse = () => {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useMutation<AdminUseSelectDto, ApiErrorWrapper, AdminUseUpdateDto>({
    mutationFn: async (dto) => {
      return await apiPost<AdminUseSelectDto>({
        body: dto,
        path: '/v1/admin/uses',
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminUses.root });
    },
  });
};

export const useAdminUpdateUse = () => {
  const { apiPatch } = useApi();
  const queryClient = useQueryClient();

  return useMutation<AdminUseSelectDto, ApiErrorWrapper, { dto: AdminUseUpdateDto; id: string }>({
    mutationFn: async ({ dto, id }) => {
      return await apiPatch<AdminUseSelectDto>({
        body: dto,
        path: `/v1/admin/uses/${id}`,
      });
    },
    onSuccess: (data) => {
      void queryClient.setQueryData(queryKeys.adminUses.getUseById(data.id), data);
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminUses.root });
    },
  });
};

export const useAdminUpdateUseVisibility = () => {
  const { apiPatch } = useApi();
  const queryClient = useQueryClient();

  return useMutation<AdminUseSelectDto, ApiErrorWrapper, { archived: boolean; id: string }>({
    mutationFn: async ({ id, archived }) => {
      return await apiPatch<AdminUseSelectDto>({
        body: { archived },
        path: `/v1/admin/uses/${id}/visibility`,
      });
    },
    onSuccess: (data) => {
      void queryClient.setQueryData(queryKeys.adminUses.getUseById(data.id), data);
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminUses.root });
    },
  });
};

export const useAdminDeleteUse = () => {
  const { apiDelete } = useApi();
  const queryClient = useQueryClient();

  return useMutation<{ success: boolean }, ApiErrorWrapper, string>({
    mutationFn: async (id) => {
      return await apiDelete<{ success: boolean }>({
        path: `/v1/admin/uses/${id}`,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminUses.getUses() });
    },
  });
};
